import { Component, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';

import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { DateAdapter, SatDatepicker, SatDatepickerRangeValue } from 'saturn-datepicker-v15';
import {DateTime} from 'luxon';

export interface ValueDateFormat {
	begin: Date;
	end: Date;
}

export interface EmittedDateRangeControl {
	begin: {
		year: number;
		month: number;
		day: number;
	};
	end: {
		year: number;
		month: number;
		day: number;
	};
	nullable: boolean;
}

@Component({
	selector: 'wncm-form-control-date-range-selector',
	templateUrl: './form-control-date-range-selector.component.html',
	styleUrls: ['./form-control-date-range-selector.component.scss']
})
export class FormControlDateRangeSelectorComponent implements OnInit {
	private _value: ValueDateFormat;

	private _emittedElement: EmittedDateRangeControl = {
		begin: {
			year: null,
			month: null,
			day: null
		},
		end: {
			year: null,
			month: null,
			day: null
		},
		nullable: false
	};

	@ViewChild('satPicker', { static: true }) dateInput: SatDatepicker<any>;

	@Input() inlineMode = false;

	@Input() minDate?: Date;

	@Input() maxDate?: Date;

	@Input() beginDate?: Date;

	@Input() endDate?: Date;

	@Input() startAt?: Date;

	@Input() rangeMode = true;

	@Input() enableArrows = false;

	@Input() placeholder = 'Sélectionner une période';

	@Input() clearInputCross = false;

	@Input() set value(val: any) {
		this._value = val;
		if (
			this.value !== undefined &&
			this.value != null &&
			this.value.begin !== undefined &&
			this.value.end !== undefined
		) {
			this.form.controls['date'].setValue({ begin: this.value.begin, end: this.value.end });
		}
	}

	@Output() public changeDateRange: EventEmitter<EmittedDateRangeControl> = new EventEmitter();

	public form: UntypedFormGroup;

	public date: SatDatepickerRangeValue<Date>;

	constructor(private _adapter: DateAdapter<any>, fb: UntypedFormBuilder) {
		this.form = fb.group({
			date: [{}]
		});
	}

	public ngOnInit(): void {
		this._adapter.setLocale('default');
	}

	private dateEventEmitter(
		beginYear: number,
		beginMonth: number,
		beginDay: number,
		endYear: number,
		endMonth: number,
		endDay: number,
		nullable = false
	): void {
		this._emittedElement.begin.year = beginYear;
		this._emittedElement.begin.month = beginMonth;
		this._emittedElement.begin.day = beginDay;

		this._emittedElement.end.year = endYear;
		this._emittedElement.end.month = endMonth;
		this._emittedElement.end.day = endDay;

		this._emittedElement.nullable = nullable;

		this.changeDateRange.emit(this._emittedElement);
	}

	public popupRangeChange(event: any): void {
		const beginDateBefore = event.target.value.begin;
		const endDateBefore = event.target.value.end;

		const beginMonth = beginDateBefore._i.month;
		// beginMonth = beginMonth + 1;
		const beginDate = beginDateBefore._i.year + '-' + beginMonth + '-' + beginDateBefore._i.date + ' 00:00:00';

		const endMonth = endDateBefore._i.month;
		// endMonth = endMonth + 1;
		const endDate = endDateBefore._i.year + '-' + endMonth + '-' + endDateBefore._i.date + ' 00:00:00';

		this.dateEventEmitter(
			beginDateBefore._i.year,
			beginMonth,
			beginDateBefore._i.date,
			endDateBefore._i.year,
			endMonth,
			endDateBefore._i.date
		);
	}

	public inlineRangeChange(event: any): void {
		const beginDateBefore = event.begin;
		const endDateBefore = event.end;
		const beginMonth = beginDateBefore._i.month;
		// beginMonth = beginMonth + 1;
		const beginDate = beginDateBefore._i.year + '-' + beginMonth + '-' + beginDateBefore._i.date + ' 00:00:00';

		const endMonth = endDateBefore._i.month;
		// endMonth = endMonth + 1;
		const endDate = endDateBefore._i.year + '-' + endMonth + '-' + endDateBefore._i.date + ' 00:00:00';

		this.dateEventEmitter(
			beginDateBefore._i.year,
			beginMonth,
			beginDateBefore._i.date,
			endDateBefore._i.year,
			endMonth,
			endDateBefore._i.date
		);
	}

	public navigatePrevious(): void {
        const howManyDays = DateTime.fromJSDate(this.endDate)
            .plus({days: 1})
            .diff(DateTime.fromJSDate(this.beginDate), 'days').days;
		// let howManyDays = dateDiff / 86400000;

        this.beginDate = DateTime.fromJSDate(this.beginDate)
            .plus({days: -howManyDays})
            .toJSDate();
        this.endDate = DateTime.fromJSDate(this.endDate)
            .plus({days: -howManyDays})
            .toJSDate();

		this.value = {
			begin: new Date(this.beginDate.getFullYear(), this.beginDate.getMonth(), this.beginDate.getDate()),
			end: new Date(this.endDate.getFullYear(), this.endDate.getMonth(), this.endDate.getDate())
		};
		this.dateEventEmitter(
			this.beginDate.getFullYear(),
			this.beginDate.getMonth(),
			this.beginDate.getDate(),
			this.endDate.getFullYear(),
			this.endDate.getMonth(),
			this.endDate.getDate()
		);
	}

	public navigateNext(): void {
        const howManyDays = DateTime.fromJSDate(this.endDate)
            .plus({days: 1})
            .diff(DateTime.fromJSDate(this.beginDate), 'days').days;
		// // let howManyDays = dateDiff / 86400000;
        this.beginDate = DateTime.fromJSDate(this.beginDate)
            .plus({days: howManyDays})
            .toJSDate();
        this.endDate = DateTime.fromJSDate(this.endDate)
            .plus({days: howManyDays})
            .toJSDate();

		this.value = {
			begin: new Date(this.beginDate.getFullYear(), this.beginDate.getMonth(), this.beginDate.getDate()),
			end: new Date(this.endDate.getFullYear(), this.endDate.getMonth(), this.endDate.getDate())
		};
		this.dateEventEmitter(
			this.beginDate.getFullYear(),
			this.beginDate.getMonth(),
			this.beginDate.getDate(),
			this.endDate.getFullYear(),
			this.endDate.getMonth(),
			this.endDate.getDate()
		);
	}

	public clear(): void {
		this.dateEventEmitter(null, null, null, null, null, null, true);
		this.beginDate = null;
		this.endDate = null;
		this.value = null;
		this.form.controls.date.setValue(null);
	}

	get value(): any {
		return this._value;
	}
}
